import { useState } from 'preact/hooks';
import { formatIngredient, type IngredientGroup } from '../../shared/recipe';
import { Segmented } from '../components/ui';
import { api, type Card, type CardType } from '../lib/api';
import { navigate } from '../lib/router';
import { errorText, toast, useAsync } from '../lib/store';

type Mode = 'url' | 'text' | 'photo' | 'blank' | 'file';

export function Capture() {
  const [mode, setMode] = useState<Mode>('url');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const types = useAsync(() => api.get<{ items: CardType[] }>('/types'), []);

  const run = async (fn: () => Promise<Card | void>) => {
    setBusy(true);
    setError(null);
    try {
      const card = await fn();
      if (card) navigate(`/c/${card.id}/edit`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const key = () => ({ 'idempotency-key': crypto.randomUUID() });

  return (
    <>
      <h1>Capture</h1>
      <Segmented<Mode>
        label="Capture method"
        value={mode}
        onChange={(m) => {
          setMode(m);
          setError(null);
        }}
        options={[
          ['url', 'Paste URL'],
          ['text', 'Paste text'],
          ['photo', 'Photo'],
          ['blank', 'Blank'],
          ['file', 'Import file'],
        ]}
      />
      <div class="panel" style={{ marginTop: 'var(--bk-space-4)' }}>
        {mode === 'url' && (
          <form
            class="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                const r = await api.post<{ card: Card; warnings: string[] }>('/import/url', { url }, key());
                r.warnings.forEach((w) => toast(w, 'error'));
                toast('Imported as a draft — check it over');
                return r.card;
              });
            }}
          >
            <label for="url">Recipe page URL</label>
            <input id="url" class="dr-input" type="url" required placeholder="https://…" value={url} onInput={(e) => setUrl(e.currentTarget.value)} />
            <p class="muted small">Bakery reads the page's schema.org Recipe data and opens it in the editor as a draft.</p>
            <button class="dr-btn dr-btn--primary" disabled={busy}>
              {busy ? 'Importing…' : 'Import'}
            </button>
          </form>
        )}

        {mode === 'text' && (
          <div class="stack">
            <label for="txt">Recipe text</label>
            <textarea
              id="txt"
              class="dr-input"
              style={{ minHeight: 'var(--measure)' }}
              placeholder={'Leek soup\nServes 4\n\nIngredients\n2 tbsp olive oil\n3 leeks, sliced\n\nMethod\n1. Soften the leeks for 10 minutes.'}
              value={text}
              onInput={(e) => {
                setText(e.currentTarget.value);
                setPreview(null);
              }}
            />
            <div class="row">
              <button class="dr-btn" disabled={busy || !text.trim()} onClick={() => run(async () => setPreview((await api.post<{ preview: any }>('/import/text', { text })).preview))}>
                Preview
              </button>
              <button class="dr-btn dr-btn--primary" disabled={busy || !text.trim()} onClick={() => run(async () => (await api.post<{ card: Card }>('/import/text', { text, save: true }, key())).card)}>
                Create draft
              </button>
            </div>
            {preview && (
              <div class="notice">
                <h3>{preview.title}</h3>
                {preview.data.ingredient_groups.map((g: IngredientGroup) => (
                  <div>
                    {g.name && <strong>{g.name}</strong>}
                    <ul>
                      {g.items.map((i) => (
                        <li>{formatIngredient(i)}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                <ol>
                  {preview.data.steps.map((s: any) => (
                    <li>
                      {s.text} {s.timer_min ? <span class="muted">⏱ {s.timer_min} min</span> : null}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <p class="muted small">Ingredients and steps are split by headings and line patterns. For an AI tidy-up, ask ChatGPT to save it via the Bakery integration.</p>
          </div>
        )}

        {mode === 'photo' && (
          <form
            class="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const f = (e.currentTarget.elements.namedItem('photo') as HTMLInputElement).files?.[0];
              const title = (e.currentTarget.elements.namedItem('ptitle') as HTMLInputElement).value || 'Photographed recipe';
              if (!f) return;
              void run(async () => {
                const { card } = await api.post<{ card: Card }>('/cards', { type: 'recipe', title, status: 'draft', change_note: 'Captured from a photo' }, key());
                const fd = new FormData();
                fd.append('role', 'gallery');
                fd.append('file', f);
                await api.upload(`/cards/${card.id}/media`, fd);
                return card;
              });
            }}
          >
            <label for="photo">Photo of a cookbook page or handwritten card</label>
            <input id="photo" name="photo" class="dr-input" type="file" accept="image/*" capture="environment" required />
            <label for="ptitle">Title</label>
            <input id="ptitle" name="ptitle" class="dr-input" placeholder="Grandma's apple cake" />
            <p class="muted small">Stored as a gallery photo on a new draft (OCR comes later). Location data is stripped.</p>
            <button class="dr-btn dr-btn--primary" disabled={busy}>
              Create draft
            </button>
          </form>
        )}

        {mode === 'blank' && (
          <div class="stack">
            <p>Choose a type:</p>
            <div class="row">
              {types.data?.items.map((t) => (
                <button class="dr-btn" onClick={() => navigate(`/new/${t.key}`)}>
                  New {t.label.toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === 'file' && (
          <form
            class="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const f = (e.currentTarget.elements.namedItem('file') as HTMLInputElement).files?.[0];
              if (!f) return;
              void run(async () => {
                const fd = new FormData();
                fd.append('file', f);
                const r = await api.upload<any>('/import/file', fd);
                if (r.result) {
                  toast(`Imported ${r.result.cards} cards (${r.result.skipped} already present), ${r.result.media} images, ${r.result.collections} collections`);
                  navigate('/browse');
                  return;
                }
                return r.card as Card;
              });
            }}
          >
            <label for="file">Bakery export (.zip), Markdown with front matter (.md), or schema.org Recipe (.json)</label>
            <input id="file" name="file" class="dr-input" type="file" accept=".zip,.md,.markdown,.json,.jsonld" required />
            <button class="dr-btn dr-btn--primary" disabled={busy}>
              {busy ? 'Importing…' : 'Import'}
            </button>
          </form>
        )}
        {error && (
          <p class="error" role="alert" style={{ marginTop: 'var(--bk-space-3)' }}>
            {error}
          </p>
        )}
      </div>
    </>
  );
}
